import { useRef } from 'react'
import { useCartStore } from '@astro/store'
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@astro/api';
import { Link } from 'react-router-dom';

/** Format number string to ₹XX,XXX */
const formatPrice = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return '₹0';
  return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

/** Strip HTML tags for plain-text preview */
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};

export default function BestsellersSection() {
  const trackRef = useRef(null)
  const add = useCartStore((s) => s.add);

  const { data, isLoading } = useQuery({
    queryKey: ["bestSelling"],
    queryFn: () => api.catalog.bestSelling(),
    placeholderData: keepPreviousData
  });

  const bestSellingProducts = data?.data || [];

  const scroll = (dir) => {
    const el = trackRef.current
    if (!el) return
    const card = el.querySelector('[data-card]')
    const amount = card ? card.offsetWidth + 24 : 320
    el.scrollBy({ left: dir * amount, behavior: 'smooth' })
  }

  return (
    <section className="relative overflow-hidden py-16 md:py-24 md:px-16 bg-white normal-case select-none">
      <div className="pointer-events-none absolute inset-0 opacity-[0.5] -z-10">
        <div className="absolute -right-20 top-0 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(232,119,15,0.08)_0%,transparent_70%)] blur-2xl animate-pulse"></div>
      </div>

      <div className="container relative z-10 mx-auto px-6 max-w-350">

        {/* header section */}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between border-b border-gray-100 pb-6 mb-12 relative">
          <div className="max-w-xl text-left">
            <span className="inline-flex items-center gap-2 text-[10px] font-medium tracking-[0.35em] text-secondary uppercase mb-2"><i className="fa-solid fa-fire h-3.5 w-3.5"></i> MOST LOVED</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-primary font-normal uppercase">Bestselling Treasures</h2>
            <p className="mt-3 text-sm text-foreground/55 font-body leading-relaxed">Our most-cherished energised gemstones and remedies, trusted by thousands of devotees across the world.</p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => scroll(-1)} aria-label="Slide Left" className="grid h-10 w-10 place-items-center rounded-full bg-gray-50 text-primary shadow-sm transition-all hover:bg-primary hover:text-white active:scale-95 border-none"><i className="fa-solid fa-chevron-left"></i></button>
            <button onClick={() => scroll(1)} aria-label="Slide Next" className="grid h-10 w-10 place-items-center rounded-full bg-white text-primary shadow-md border border-gray-100 transition-all hover:bg-secondary hover:text-white active:scale-95"><i className="fa-solid fa-chevron-right"></i></button>
          </div>
        </div>

        {/* card section */}
        <div ref={trackRef} className="relative overflow-x-auto scrollbar-none">
          <div className="flex gap-6 w-max py-2">
            {isLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} data-card className="w-70 md:w-77.5 shrink-0 rounded-3xl border border-gray-100 bg-white overflow-hidden animate-pulse">
                    <div className="aspect-square bg-gray-200" />
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between">
                        <div className="flex gap-1">{Array.from({ length: 5 }).map((_, j) => <div key={j} className="h-3 w-3 rounded-full bg-gray-200" />)}</div>
                        <div className="h-3 w-20 rounded bg-gray-100" />
                      </div>
                      <div className="h-5 w-40 rounded bg-gray-200" />
                      <div className="h-3 w-full rounded bg-gray-100" />
                      <div className="h-3 w-3/4 rounded bg-gray-100" />
                      <div className="pt-4 border-t border-dashed border-gray-100 flex justify-between items-center">
                        <div className="space-y-1">
                          <div className="h-2 w-16 rounded bg-gray-100" />
                          <div className="h-6 w-24 rounded bg-gray-200" />
                        </div>
                        <div className="h-3 w-16 rounded bg-gray-100" />
                      </div>
                    </div>
                  </div>
                ))
              : bestSellingProducts.map((p) => {
                  const category = p.categories?.find(c => c.parent_id === null)?.name || p.categories?.[0]?.name || '';
                  const hasDiscount = parseFloat(p.discount_rate) > 0;
                  const inStock = parseInt(p.stock) > 0;

                  return (
                    <div key={p.id} data-card className="group relative bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-primary/5 flex flex-col font-normal uppercase tracking-tight w-70 md:w-77.5 shrink-0 border border-gray-100 outline-none transition-all duration-300 hover:border-secondary/40">

                      <div className="relative aspect-square overflow-hidden bg-muted/10 flex items-center justify-center">
                        <img alt={p.name} loading="lazy" className="object-contain w-3/4 h-3/4 transition-transform duration-1000 group-hover:scale-110" src={p.image_url} />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-3 z-20">
                          <button
                            onClick={() => inStock && add({ productId: p.id, title: p.name, price: { amount: parseFloat(p.total_price) * 100, currency: 'INR' }, qty: 1 })}
                            disabled={!inStock}
                            className="inline-flex items-center justify-center px-6 py-2 w-48 h-11 rounded-xl transition-all duration-300 font-semibold tracking-[0.2em] text-[9px] shadow-2xl uppercase bg-white text-primary hover:bg-secondary hover:text-white border-none disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-primary"
                          >
                            {inStock ? (<>ADD TO BASKET <i className="fa-solid fa-shopping-bag ml-2"></i></>) : (<>OUT OF STOCK <i className="fa-solid fa-ban ml-2"></i></>)}
                          </button>
                          <Link to={`/products/${p.slug}`} className="w-48 h-11 rounded-xl bg-transparent border border-white/40 text-white flex items-center justify-center gap-2 text-[9px] tracking-[0.2em] hover:bg-white/10 transition-all backdrop-blur-sm">VIEW DETAILS <i className="fa-solid fa-arrow-up-right ml-1"></i></Link>
                        </div>
                        {category && (
                          <div className="absolute bottom-4 left-4 z-10"><span className="bg-primary text-white px-3 py-1 rounded-lg text-[8px] font-normal tracking-widest border border-white/10 uppercase shadow-lg">{category}</span></div>
                        )}
                        {hasDiscount && (
                          <div className="absolute top-4 right-4 z-10"><span className="bg-secondary text-white px-3 py-1 rounded-lg text-[8px] font-bold tracking-widest border border-white/10 uppercase shadow-lg">{parseFloat(p.discount_rate)}% OFF</span></div>
                        )}
                      </div>

                      <div className="p-6 flex flex-col flex-1 space-y-5 justify-between">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-0.5 text-secondary text-xs">
                              {Array.from({ length: 5 }).map((_, i) => <i key={i} className="fa-solid fa-star"></i>)}
                            </div>
                            <div className="flex items-center gap-1.5 opacity-40 text-primary"><span className="text-[8px] font-bold tracking-widest uppercase">✓ LAB VERIFIED</span></div>
                          </div>
                          <h3 className="text-base font-heading text-primary leading-tight tracking-tight group-hover:text-secondary transition-colors font-semibold uppercase line-clamp-1">{p.name}</h3>
                          <p className="text-[11px] text-primary/50 font-body leading-relaxed line-clamp-2 normal-case">{stripHtml(p.sort_description)}</p>
                        </div>
                        <div className="pt-4 border-t border-dashed border-gray-100 flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="text-[8px] text-primary/30 uppercase tracking-[0.2em]">SYSTEM PRICE</span>
                            <div className="flex items-baseline gap-2">
                              <span className="text-xl font-bold text-primary">{formatPrice(p.total_price)}</span>
                              {hasDiscount && <span className="text-xs text-primary/30 line-through">{formatPrice(p.product_price)}</span>}
                            </div>
                          </div>
                          <div className={`flex items-center gap-1.5 ${inStock ? 'text-green-500' : 'text-red-400'}`}>
                            <span className="text-[8px] font-bold tracking-widest uppercase opacity-60">{inStock ? 'AVAILABLE' : 'OUT OF STOCK'}</span>
                            <div className={`h-1.5 w-1.5 rounded-full ${inStock ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
            }
          </div>
        </div>
      </div>
    </section>
  )
}
